import { useNavigate, useRouterState } from "@tanstack/react-router"

type ProjectRouteParams = { projectId: string }
type StyleRouteParams = { styleId: string }

type BaseNavigateOptions = {
	search?: unknown
	hash?: string
	replace?: boolean
	[key: string]: unknown
}

type RouterLike = {
	navigate: (options: Record<string, unknown>) => Promise<void>
	state: {
		location: {
			search: unknown
			hash: string
		}
	}
}

const withContext = (
	defaultSearch: unknown,
	defaultHash: string,
	options?: BaseNavigateOptions,
): BaseNavigateOptions => {
	const { search, hash, ...rest } = options ?? {}

	return {
		...rest,
		search: search ?? defaultSearch,
		hash: hash ?? defaultHash,
	}
}

const staticRoute = (path: string) => ({
	path,
	build: () => path,
	useNavigate: () => {
		const navigate = useNavigate()
		const { search: currentSearch, hash: currentHash } = useRouterState({
			select: (state) => ({
				search: state.location.search,
				hash: state.location.hash,
			}),
		})

		return (options?: BaseNavigateOptions) => {
			const merged = withContext(currentSearch, currentHash, options)

			return navigate({
				to: path,
				...merged,
			} as Record<string, unknown>)
		}
	},
	navigate: (router: RouterLike, options?: BaseNavigateOptions) => {
		const merged = withContext(
			router.state.location.search,
			router.state.location.hash,
			options,
		)

		return router.navigate({
			to: path,
			...merged,
		})
	},
})

const dynamicProjectRoute = (path: string) => {
	const [pathPrefix] = path.split("$projectId")

	return {
		path,
		pathPrefix: pathPrefix ?? path,
		build: ({ projectId }) => path.replace("$projectId", projectId),
		params: () => ({ projectId: "" }) as ProjectRouteParams,
		useNavigate: () => {
			const navigate = useNavigate()
			const { search: currentSearch, hash: currentHash } = useRouterState({
				select: (state) => ({
					search: state.location.search,
					hash: state.location.hash,
				}),
			})

			return (params: ProjectRouteParams, options?: BaseNavigateOptions) => {
				const merged = withContext(currentSearch, currentHash, options)

				return navigate({
					to: path,
					params,
					...merged,
				} as Record<string, unknown>)
			}
		},
		navigate: (
			router: RouterLike,
			params: ProjectRouteParams,
			options?: BaseNavigateOptions,
		) => {
			const merged = withContext(
				router.state.location.search,
				router.state.location.hash,
				options,
			)

			return router.navigate({
				to: path,
				params,
				...merged,
			})
		},
	}
}

const dynamicStyleRoute = (path: string) => {
	const [pathPrefix] = path.split("$styleId")

	return {
		path,
		pathPrefix: pathPrefix ?? path,
		build: ({ styleId }: StyleRouteParams) => path.replace("$styleId", styleId),
		params: () => ({ styleId: "" }) as StyleRouteParams,
		useNavigate: () => {
			const navigate = useNavigate()
			const { search: currentSearch, hash: currentHash } = useRouterState({
				select: (state) => ({
					search: state.location.search,
					hash: state.location.hash,
				}),
			})

			return (params: StyleRouteParams, options?: BaseNavigateOptions) => {
				const merged = withContext(currentSearch, currentHash, options)

				return navigate({
					to: path,
					params,
					...merged,
				} as Record<string, unknown>)
			}
		},
		navigate: (router: RouterLike, params: StyleRouteParams, options?: BaseNavigateOptions) => {
			const merged = withContext(
				router.state.location.search,
				router.state.location.hash,
				options,
			)

			return router.navigate({
				to: path,
				params,
				...merged,
			})
		},
	}
}

export const routes = {
	root: staticRoute("/"),
	styles: staticRoute("/styles"),
	styleCreate: staticRoute("/styles/create"),
	styleDetail: dynamicStyleRoute("/styles/$styleId"),
	tutorials: staticRoute("/tutorials"),
	support: staticRoute("/support"),
	settings: staticRoute("/settings"),
	account: staticRoute("/account"),
	project: dynamicProjectRoute("/projects/$projectId"),
	projects: staticRoute("/projects"),
} as const

export type RoutePath = ReturnType<typeof routes.root.build>

export type ProjectRouteParamsType = Parameters<typeof routes.project.build>[0]
